import { useMutationState } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useOutletContext } from 'react-router'
import { PageFooter } from '@/components/commons/page-footer'
import { PageHero } from '@/components/commons/page-hero'
import { ButtonBrandLegacy } from '@/components/ui/button-brand-legacy'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { CheckIcon } from '@/components/ui/icons'
import { AiVettingConfirmManualRetouchingModal } from '@/features/photoset/components/ai-vetting-confirm-manual-retouching-modal/ai-vetting-confirm-manual-retouching-modal'
import { AiVettingConfirmModal } from '@/features/photoset/components/ai-vetting-confirm-modal/ai-vetting-confirm-modal'
import { AiVettingSelectionSpaceImages } from '@/features/photoset/components/ai-vetting-selection-space-images/ai-vetting-selection-space-images'
import { GalleryAiVettingModal } from '@/features/photoset/components/gallery-photoset-modal/gallery-ai-vetting-modal/gallery-ai-vetting-modal'
import { PhotosetGenerating } from '@/features/photoset/components/photoset-loading'
import { PhotosetSpaceElement } from '@/features/photoset/components/photoset-space-element'
import {
	PhotosetStepBodyContainer,
	PhotosetStepWrapper
} from '@/features/photoset/components/photoset-step-atoms'
import { StepHeroControls } from '@/features/photoset/components/step-hero-controls'
import { useExpandPhotosetImage } from '@/features/photoset/hooks/use-expand-photoset-image'
import { useGetPhotoset } from '@/features/photoset/queries/use-get-photoset'
import { GET_PHOTOSET_IMAGES_LIST_RESPONSE_TYPE } from '@/features/photoset/queries/use-get-photoset-images-list'
import { useGetPhotosetSpaces } from '@/features/photoset/queries/use-get-photoset-spaces'
import { usePhotosetStore } from '@/features/photoset/store/store.ts'
import type { PhotosetURLParams } from '@/features/photoset/types.ts'
import { MUTATION_KEYS } from '@/query-client'
import { useTranslation } from '@/translations/i18n'

export const PhotosetAiVettingStep = () => {
	const { t } = useTranslation()
	const { urlParams } = useOutletContext<{ urlParams: PhotosetURLParams }>()
	const { data: photoset } = useGetPhotoset(
		{ id: urlParams.id },
		{ enabled: false }
	)
	const {
		data: spaces,
		isFetching,
		isError
	} = useGetPhotosetSpaces({
		urlParams: {
			photosetId: urlParams.id
		},
		params: {
			is_final_select: true,
			response_type: GET_PHOTOSET_IMAGES_LIST_RESPONSE_TYPE
		}
	})

	//photoset store
	const photosetSpaces = usePhotosetStore((state) => state.photosetSpaces)
	const initPhotosetSpaces = usePhotosetStore(
		(state) => state.initPhotosetSpaces
	)

	//statuses
	const [showConfirmationModal, setShowConfirmationModal] = useState(false)

	// Init photoset store
	useEffect(() => {
		if (spaces) {
			initPhotosetSpaces(spaces)
		}
	}, [spaces, initPhotosetSpaces])

	// Handle gallery modal view
	const {
		currentDetailImageIndex,
		setCurrentDetailImageIndex,
		currentDetailSpaceIndex,
		currentDetailSpace,
		onExpandImage,
		onReduceImage
	} = useExpandPhotosetImage()

	// Get selected image
	const needManualRetouchingImages = useMemo(() => {
		return photosetSpaces.flatMap((space) =>
			space.images.filter((image) => image.need_manual_retouching)
		)
	}, [photosetSpaces])

	// Retrive update status
	const updateStepToAiVettingCompletedStatus = useMutationState({
		filters: {
			mutationKey: [MUTATION_KEYS.UPDATE_STEP_AI_VETTING_COMPLETED]
		},
		select: (mutation) => mutation.state.status
	})

	const updateStepToManualRetouchingStatus = useMutationState({
		filters: {
			mutationKey: [MUTATION_KEYS.UPDATE_STEP_AI_MANUAL_RETOUCHING]
		},
		select: (mutation) => mutation.state.status
	})

	const isUpdateStepPending = useMemo(
		() =>
			updateStepToAiVettingCompletedStatus.includes('pending') ||
			updateStepToManualRetouchingStatus.includes('pending'),
		[
			updateStepToAiVettingCompletedStatus,
			updateStepToManualRetouchingStatus
		]
	)

	if (!(photoset?.ai_retouched_thumbnail_generated_at || isFetching)) {
		return (
			<PhotosetGenerating
				subtitle={t('photoset:imagen_ai.in_progress.paragraph')}
				title={t('photoset:imagen_ai.in_progress.title')}
			/>
		)
	}

	return (
		<>
			<PageHero
				endSlot={<StepHeroControls hideSortControls />}
				subtitle={t('photoset:ai_vetting.subtitle')}
				title={t('photoset:ai_vetting.title')}
			/>

			{showConfirmationModal &&
				(needManualRetouchingImages.length > 0 ? (
					<AiVettingConfirmManualRetouchingModal
						needManualRetouchingImages={needManualRetouchingImages}
						onClose={() => setShowConfirmationModal(false)}
						photosetId={urlParams.id}
					/>
				) : (
					<AiVettingConfirmModal
						onClose={() => setShowConfirmationModal(false)}
						photosetId={urlParams.id}
					/>
				))}

			<PhotosetStepWrapper>
				<PhotosetStepBodyContainer $disabled={isUpdateStepPending}>
					{isFetching &&
						[0, 1, 2].map((e) => (
							<PhotosetSpaceElement asSkeleton key={`${e}-id`} />
						))}
					{!(isFetching || isError) &&
						photosetSpaces?.map(
							(photosetSpace, spaceIndex) =>
								photosetSpace.images.length > 0 && (
									<PhotosetSpaceElement
										endSlot={
											<AiVettingSelectionSpaceImages
												images={photosetSpace.images}
												onExpandImage={onExpandImage}
												photosetId={urlParams.id}
												photosetSpaceId={
													photosetSpace.id
												}
												spaceIndex={spaceIndex}
											/>
										}
										key={photosetSpace.id}
										spaceName={photosetSpace.space.key_name}
									/>
								)
						)}
				</PhotosetStepBodyContainer>

				<PageFooter
					endSlot={
						needManualRetouchingImages.length > 0 ? (
							<ButtonBrandLegacy
								onClick={() =>
									setShowConfirmationModal(
										!showConfirmationModal
									)
								}
							>
								{t('photoset:ai_vetting.manual_retouching_cta')}
							</ButtonBrandLegacy>
						) : (
							<ButtonLegacy
								disabled={isUpdateStepPending}
								onClick={() =>
									setShowConfirmationModal(
										!showConfirmationModal
									)
								}
								type='button'
								variant='primarySuccess'
							>
								{t('common:done')}
								<CheckIcon />
							</ButtonLegacy>
						)
					}
				/>
			</PhotosetStepWrapper>

			{currentDetailImageIndex !== null &&
				currentDetailSpaceIndex !== null &&
				currentDetailSpace && (
					<GalleryAiVettingModal
						currentDetailImageIndex={currentDetailImageIndex}
						currentDetailSpaceIndex={currentDetailSpaceIndex}
						currentSpaceId={currentDetailSpace.id}
						currentSpaceName={currentDetailSpace.space.key_name}
						onClose={onReduceImage}
						selectableImages={currentDetailSpace.images}
						setCurrentDetailImageIndex={setCurrentDetailImageIndex}
					/>
				)}
		</>
	)
}

PhotosetAiVettingStep.displayName = 'PhotosetAiVettingStep'
